import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import AuthImage from "./AuthImage";

interface MatchDetail {
    code: string;
    component: string;
    value: number;
}

export interface CompatibleItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    categoryId?: string | null;
    primaryPhotoUrl: string | null;
    score: number;
    matchReasons: string[];
    // pair-compat-v2 (additive): взвешенный балл [0,1], пометка «не оценена», причины для чипов.
    pairScore?: number | null;
    unscored?: boolean;
    matchDetails?: MatchDetail[];
}

interface Result {
    sourceItemId: string;
    items: CompatibleItem[];
}

interface Props {
    itemId: string | undefined;
    ownerId: string | null;
    /**
     * Слотовый контекст (UX-аудит F01): если задан, основное нажатие карточки ставит вещь
     * в слот, а «Подробнее» остаётся отдельным действием. Без него основное нажатие —
     * открыть карточку вещи (вкладка «Образы», где терять нечего).
     */
    onPick?: (item: CompatibleItem) => void;
    /** Подпись основного действия слотового режима: «Добавить» или «Заменить». */
    pickLabel?: string;
}

// Подписи чипов по коду причины (matchDetails.code → человекочитаемый текст).
const DETAIL_LABELS: Record<string, string> = {
    COLOR_HARMONY_MONOCHROME: 'Цвета в тон',
    COLOR_HARMONY_ANALOGOUS: 'Близкие цвета',
    COLOR_HARMONY_SPLIT: 'Мягкий контраст',
    COLOR_HARMONY_COMPLEMENTARY: 'Цветовой контраст',
    COLOR_HARMONY_NEUTRAL: 'Нейтральная база',
    STYLE_MATCH: 'Общий стиль',
    FORMALITY_MATCH: 'Формальность в тон',
    SILHOUETTE_BALANCE: 'Баланс силуэта',
    MATERIAL_COHERENCE: 'Сочетание фактур',
    PATTERN_BALANCE: 'Баланс принтов',
    SEASON_OVERLAP: 'Общий сезон',
};

const MAX_REASON_CHIPS = 2;

const CompatibleItems: React.FC<Props> = ({itemId, ownerId, onPick, pickLabel = 'Добавить'}) => {
    const navigate = useNavigate();
    const [items, setItems] = useState<CompatibleItem[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!itemId) {
            return;
        }
        let cancelled = false;
        (async () => {
            setFailed(false);
            try {
                const api = await createAxiosInstance();
                const params = ownerId ? {ownerId} : {};
                const res = await api.get<Result>(`/compatibility/${itemId}`, {params});
                if (!cancelled) setItems(res.data.items);
            } catch (e) {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [itemId, ownerId]);

    // Неоценённые пары скрываются (pair-compat-v2, UI-решение из requirements);
    // сортировка — по новому парному скору (fallback на legacy score для старых ответов).
    const visible = (items ?? [])
        .filter(it => !it.unscored)
        .sort((a, b) => (b.pairScore ?? b.score / 100) - (a.pairScore ?? a.score / 100));

    // Секция показывается только когда есть с чем сочетать.
    if (failed || visible.length === 0) {
        return null;
    }

    const reasonChips = (it: CompatibleItem): string[] => {
        const fromDetails = (it.matchDetails ?? [])
            .map(d => DETAIL_LABELS[d.code])
            .filter((label): label is string => Boolean(label));
        const labels = fromDetails.length > 0 ? fromDetails : it.matchReasons;
        return labels.slice(0, MAX_REASON_CHIPS);
    };

    const openDetails = (it: CompatibleItem) => navigate(`/clothing/${it.id}`);
    const primaryAction = (it: CompatibleItem) => (onPick ? onPick(it) : openDetails(it));
    const primaryLabel = (it: CompatibleItem) =>
        (onPick ? `${pickLabel}: ${it.name}` : `Открыть карточку вещи «${it.name}»`);

    return (
        <section className="mt-6" data-testid="compatible-items">
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">С чем носить</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {visible.map(it => (
                    <div key={it.id}
                         className="rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden hover:border-brand-500">
                        <button
                            type="button"
                            data-testid="compatible-item-primary"
                            onClick={() => primaryAction(it)}
                            aria-label={primaryLabel(it)}
                            className="block w-full text-left"
                        >
                            <span className="block aspect-square bg-gray-100 dark:bg-ink-800">
                                {it.primaryPhotoUrl && (
                                    <AuthImage imageKey={it.primaryPhotoUrl} alt={it.name} variant="thumbnail"
                                               className="w-full h-full object-cover"/>
                                )}
                            </span>
                            <span className="block p-2">
                                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {it.name}
                                </span>
                                {it.categoryName && (
                                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {it.categoryName}
                                    </span>
                                )}
                                {reasonChips(it).length > 0 && (
                                    <span className="flex flex-wrap gap-1 mt-1">
                                        {reasonChips(it).map(label => (
                                            <span key={label}
                                                  className="inline-flex items-center rounded-full bg-gray-100 dark:bg-ink-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                                                {label}
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </span>
                        </button>
                        {onPick && (
                            <div className="px-2 pb-2">
                                <button
                                    type="button"
                                    data-testid="compatible-item-details"
                                    onClick={() => openDetails(it)}
                                    aria-label={`Подробнее о вещи «${it.name}»`}
                                    className="min-h-[44px] w-full text-xs text-gray-500 dark:text-gray-400
                                               hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                                >
                                    Подробнее
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

export default CompatibleItems;
